import React, { useState, useRef, useEffect } from "react";
import { TextField, Select, MenuItem } from "@material-ui/core";
import { makeStyles, Theme } from "@material-ui/core/styles";
import { useTranslation } from "react-i18next";
import { SPECIAL_STATUS_OPTIONS } from "../constants";

const useStyles = makeStyles((theme: Theme) => ({
  cellButton: {
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: theme.spacing(0.5, 1),
    textAlign: "left",
    width: "100%",
    fontSize: theme.typography.body2.fontSize,
    borderRadius: theme.shape.borderRadius,
    color: theme.palette.text.primary,
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  placeholder: {
    color: theme.palette.text.disabled,
    fontSize: theme.typography.caption.fontSize,
  },
  input: {
    "& .MuiInputBase-input": {
      fontSize: theme.typography.body2.fontSize,
      padding: theme.spacing(0.5, 1),
    },
  },
  select: {
    fontSize: theme.typography.body2.fontSize,
    minWidth: 140,
    "& .MuiSelect-select": {
      padding: theme.spacing(0.5, 3, 0.5, 1),
    },
  },
}));

interface EditableCellProps {
  value: string | null;
  field: string;
  rowId: number;
  type: "text" | "select" | "date" | "month";
  onSave: (id: number, field: string, value: string | null) => void;
}

const EditableCell: React.VFC<EditableCellProps> = ({ value, field, rowId, type, onSave }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value || "");
  const inputRef = useRef<HTMLInputElement>(null);
  const savingRef = useRef(false);

  useEffect(() => {
    setEditValue(value || "");
  }, [value]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  const handleSave = () => {
    if (savingRef.current) return;
    savingRef.current = true;
    const newValue = editValue.trim() || null;
    if (newValue !== (value || null)) {
      onSave(rowId, field, newValue);
    }
    setIsEditing(false);
    // Reset after current event cycle to prevent blur from re-triggering
    requestAnimationFrame(() => {
      savingRef.current = false;
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSave();
    } else if (e.key === "Escape") {
      setEditValue(value || "");
      setIsEditing(false);
    }
  };

  if (type === "select") {
    return (
      <Select
        value={value || ""}
        onChange={e => onSave(rowId, field, (e.target.value as string) || null)}
        className={classes.select}
        displayEmpty
        variant="standard"
        disableUnderline
      >
        {SPECIAL_STATUS_OPTIONS.map(opt => (
          <MenuItem key={opt.value} value={opt.value}>
            {opt.label}
          </MenuItem>
        ))}
      </Select>
    );
  }

  if (isEditing) {
    return (
      <TextField
        inputRef={inputRef}
        type={type === "month" ? "month" : type === "date" ? "date" : "text"}
        value={editValue}
        onChange={e => setEditValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleSave}
        size="small"
        variant="outlined"
        className={classes.input}
        fullWidth
      />
    );
  }

  const placeholder =
    type === "date" || type === "month" ? t("referral_tracking.set_date") : t("referral_tracking.add_value");

  return (
    <button className={classes.cellButton} onClick={() => setIsEditing(true)}>
      {value || <span className={classes.placeholder}>{placeholder}</span>}
    </button>
  );
};

export default EditableCell;
